import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SheetProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
}

export const Sheet: React.FC<SheetProps> = ({ open, onClose, children, title }) => {
    // Close on ESC key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && open) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onClose]);

    // Prevent body scroll when sheet is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
            {/* Backdrop */}
            <div
                className={cn(
                    "absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity",
                    open ? "opacity-100" : "opacity-0"
                )}
                onClick={onClose}
            />

            {/* Sheet Content */}
            <div
                className={cn(
                    "relative w-full bg-card border-t border-border sm:border sm:rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out",
                    "max-h-[85vh] sm:max-h-[80vh] sm:max-w-2xl sm:rounded-2xl overflow-hidden",
                    open ? "translate-y-0" : "translate-y-full sm:translate-y-0 sm:scale-95 sm:opacity-0"
                )}
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-foreground">{title || 'Settings'}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        aria-label="Close settings"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto max-h-[calc(85vh-5rem)] sm:max-h-[calc(80vh-5rem)]">
                    {children}
                </div>
            </div>
        </div>
    );
};
